import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Calendar, Clock, ArrowRight } from "lucide-react";
import { motion } from "framer-motion";
import { Reveal } from "@/components/Reveal";
import { MaskText } from "@/components/MaskText";
import { Seo } from "@/components/Seo";
import { AFFILIATION } from "@/data/site";
import { BLOG_POSTS, getPostBySlug } from "@/data/blog";

const formatDate = (d) =>
  new Date(d).toLocaleDateString("it-IT", { day: "numeric", month: "long", year: "numeric" });

export default function BlogPost() {
  const { slug } = useParams();
  const post = getPostBySlug(slug);
  const others = BLOG_POSTS.filter((p) => p.slug !== slug).slice(0, 3);

  if (!post) {
    return (
      <div className="min-h-[70vh] grid place-items-center px-6" data-testid="blog-not-found">
        <div className="text-center">
          <h1 className="font-heading text-3xl font-bold">Articolo non trovato</h1>
          <Link
            to="/blog"
            className="mt-6 inline-flex items-center gap-2 text-brand-red font-semibold"
          >
            <ArrowLeft className="h-4 w-4" /> Torna al Blog
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="blog-post-page">
      <Seo
        title={`${post.title} | TopAffiliation`}
        description={post.excerpt}
        path={`/blog/${post.slug}`}
        image={post.image}
        type="article"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "BlogPosting",
          headline: post.title,
          description: post.excerpt,
          image: post.image,
          datePublished: post.date,
          articleSection: post.category,
          inLanguage: "it-IT",
          author: { "@type": "Organization", name: "TopAffiliation" },
          publisher: {
            "@type": "Organization",
            name: "TopAffiliation",
            logo: {
              "@type": "ImageObject",
              url: "https://www.topaffiliation.net/logo.png",
            },
          },
          mainEntityOfPage: `https://www.topaffiliation.net/blog/${post.slug}`,
        }}
      />
      <article className="pt-32 md:pt-44 pb-20">
        <div className="max-w-3xl mx-auto px-6">
          <Link
            to="/blog"
            data-testid="back-to-blog"
            className="inline-flex items-center gap-2 text-sm text-white/60 hover:text-brand-red transition-colors"
          >
            <ArrowLeft className="h-4 w-4" /> Torna al Blog
          </Link>

          <div className="mt-8 flex items-center gap-4 text-xs text-white/50">
            <span className="font-semibold uppercase tracking-widest text-brand-red">
              {post.category}
            </span>
            <span className="inline-flex items-center gap-1">
              <Calendar className="h-3.5 w-3.5" /> {formatDate(post.date)}
            </span>
            <span className="inline-flex items-center gap-1">
              <Clock className="h-3.5 w-3.5" /> {post.read_time} min
            </span>
          </div>

          <h1 className="mt-6 font-heading text-3xl md:text-5xl font-extrabold leading-[1.05] tracking-[-0.02em]">
            <MaskText trigger="load" delay={0.08} lines={[post.title]} />
          </h1>
          <p className="mt-6 text-lg text-white/60 leading-relaxed">{post.excerpt}</p>
        </div>

        <div className="max-w-4xl mx-auto px-6 mt-12">
          <motion.div
            initial={{ opacity: 0, scale: 1.03 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
            className="overflow-hidden rounded-[1.5rem] border border-white/10 aspect-video"
          >
            <img src={post.image} alt={post.title} className="w-full h-full object-cover" />
          </motion.div>
        </div>

        <div className="max-w-3xl mx-auto px-6 mt-14">
          <div
            className="prose-italia"
            data-testid="blog-post-content"
            dangerouslySetInnerHTML={{ __html: post.content }}
          />

          <div className="mt-16 rounded-2xl border border-brand-red/20 bg-gradient-to-br from-[#1C0E10] to-[#0A0A0A] p-8 md:p-10 text-center">
            <h3 className="font-heading text-2xl md:text-3xl font-bold">
              Trasforma le tue letture in guadagni
            </h3>
            <p className="mt-3 text-white/60">
              Iscriviti gratis a TopAffiliation e inizia a monetizzare il tuo traffico oggi
              stesso.
            </p>
            <a
              href={AFFILIATION.signup}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-glow mt-6 inline-flex items-center gap-2 rounded-full bg-brand-red px-7 py-3.5 text-sm font-semibold text-white hover:bg-brand-redDark hover:scale-[1.03]"
            >
              Iscriviti subito <ArrowRight className="h-4 w-4" />
            </a>
          </div>
        </div>
      </article>

      {others.length > 0 && (
        <section className="pb-28 border-t border-white/10 pt-16">
          <div className="container-x">
            <h2 className="font-heading text-2xl md:text-3xl font-bold mb-10">Ultime news</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {others.map((p, i) => (
                <Reveal key={p.slug} delay={i * 0.08}>
                  <Link to={`/blog/${p.slug}`} className="group block h-full">
                    <div className="overflow-hidden rounded-2xl border border-white/10 aspect-[16/10]">
                      <img
                        src={p.image}
                        alt={p.title}
                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                      />
                    </div>
                    <span className="mt-4 inline-block text-xs font-semibold uppercase tracking-widest text-brand-red">
                      {p.category}
                    </span>
                    <h3 className="mt-2 font-heading text-lg font-bold leading-snug group-hover:text-brand-red transition-colors">
                      {p.title}
                    </h3>
                  </Link>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
